import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDays, format, isValid, parseISO } from 'date-fns';
import { defaultMaxDate, defaultToday, PRICELINE_DATE_FORMAT } from '../constants/dates';
import getInitialHoveredDate from '../utils/getInitialHoveredDate';
import keyNavHandler from '../utils/keyNavHandler';
function useCalendarForm(_a) {
  var _b = _a.allowSameDay,
    allowSameDay = _b === void 0 ? false : _b,
    _c = _a.didUseKeyNavigation,
    initialDidUseKeyNavigation = _c === void 0 ? false : _c,
    _d = _a.endDate,
    initialEndDate = _d === void 0 ? null : _d,
    isFlightCalendar = _a.isFlightCalendar,
    _e = _a.maxDate,
    maxDate = _e === void 0 ? defaultMaxDate : _e,
    maxDaysToSelect = _a.maxDaysToSelect,
    _f = _a.maxSelectableDate,
    initialMaxSelectableDate = _f === void 0 ? defaultMaxDate : _f,
    _g = _a.minDate,
    minDate = _g === void 0 ? defaultToday : _g,
    _h = _a.scrollDate,
    scrollDate = _h === void 0 ? 0 : _h,
    _j = _a.selectTwoDates,
    selectTwoDates = _j === void 0 ? true : _j,
    _k = _a.startDate,
    initialStartDate = _k === void 0 ? null : _k,
    _l = _a.onChange,
    onChange = _l === void 0 ? function () {} : _l,
    _m = _a.onDismiss,
    onDismiss = _m === void 0 ? function () {} : _m,
    setCalendarRef = _a.setCalendarRef,
    setCalenderRef = _a.setCalenderRef;
  var initialHoveredDate = useMemo(function () {
    return getInitialHoveredDate({
      endDate: initialEndDate,
      minDate: minDate,
      selectTwoDates: selectTwoDates,
      startDate: initialStartDate
    });
  }, []);
  var calendarRef = useRef(null);
  var _o = useState(initialStartDate),
    startDate = _o[0],
    setStartDate = _o[1];
  var _p = useState(initialEndDate),
    endDate = _p[0],
    setEndDate = _p[1];
  var _q = useState(initialHoveredDate),
    hoveredDate = _q[0],
    setHoveredDate = _q[1];
  var _r = useState(maxDate || initialMaxSelectableDate),
    maxSelectableDate = _r[0],
    setMaxSelectableDate = _r[1];
  var _s = useState(initialDidUseKeyNavigation),
    didUseKeyNavigation = _s[0],
    setDidUseKeyNavigation = _s[1];
  var _t = useState(false),
    tabClose = _t[0],
    setTabClose = _t[1];
  useEffect(function () {
    var callback = setCalendarRef || setCalenderRef;
    callback === null || callback === void 0 ? void 0 : callback(calendarRef.current);
  }, [calendarRef]);
  var calculateMaxSelectableDate = useCallback(function (date) {
    var newMaxSelectableDate;
    if (maxDaysToSelect > 1) {
      newMaxSelectableDate = format(addDays(parseISO(date), maxDaysToSelect - 2), PRICELINE_DATE_FORMAT);
    } else if (isValid(date) && date instanceof Date && !isFlightCalendar) {
      // 30 as a default value
      newMaxSelectableDate = format(addDays(date, 30), PRICELINE_DATE_FORMAT);
    } else {
      newMaxSelectableDate = maxDate || defaultMaxDate;
    }
    setMaxSelectableDate(newMaxSelectableDate);
  }, [isFlightCalendar, maxDate, maxDaysToSelect, maxSelectableDate]);
  var handleKeyDismiss = useCallback(function (evt) {
    if (evt.keyCode === 9 && tabClose) {
      onDismiss(evt);
    } else if (evt.keyCode === 27) {
      evt.preventDefault();
      evt.stopPropagation();
      onDismiss(evt);
    }
  }, [tabClose]);
  var onDayClicked = useCallback(function (event, date) {
    if (selectTwoDates) {
      var isInitialClick = Boolean(startDate && endDate || !startDate || date < startDate);
      var isEndDateClick = startDate && Boolean(allowSameDay ? date >= startDate : date > startDate);
      if (isInitialClick) {
        calculateMaxSelectableDate(date);
        setStartDate(date);
        setEndDate(null);
        setHoveredDate(date);
        onChange(event, {
          startDate: date,
          endDate: null
        });
      } else if (isEndDateClick) {
        calculateMaxSelectableDate(startDate);
        setEndDate(date);
        onChange(event, {
          startDate: startDate,
          endDate: date
        });
      }
    } else {
      onChange(event, {
        startDate: date
      });
      setStartDate(date);
      setEndDate(null);
      calculateMaxSelectableDate(startDate);
    }
  }, [startDate, endDate, allowSameDay, calculateMaxSelectableDate, onChange, selectTwoDates]);
  var onDayHovered = useCallback(function (event, hoveredDate, keyNav) {
    if (endDate && !keyNav) {
      return;
    }
    var isSingleDateSelected = !selectTwoDates && startDate;
    var isBothDatesSelected = selectTwoDates && startDate && endDate;
    if (keyNav && (isSingleDateSelected || isBothDatesSelected)) {
      setStartDate(0);
      setEndDate(0);
    }
    if (!didUseKeyNavigation && keyNav) {
      setDidUseKeyNavigation(true);
    }
    setHoveredDate(hoveredDate);
  }, [didUseKeyNavigation, endDate, selectTwoDates, startDate]);
  var handleKeyNav = useCallback(function (event) {
    // Only preventDefault if it's an action that we've accounted for (e.g. don't prevent tabbing away)
    var _a = keyNavHandler(event, hoveredDate, maxSelectableDate, minDate),
      action = _a.action,
      date = _a.date;
    if (action) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (date && action === 'NAVIGATE') {
      onDayHovered(event, date, true);
    } else if (date && action === 'SELECT') {
      onDayClicked(event, date);
    }
  }, [hoveredDate, maxSelectableDate, minDate, onDayClicked, onDayHovered]);
  useEffect(function () {
    if (startDate && !endDate) {
      calculateMaxSelectableDate(startDate);
    }
  }, []);
  return {
    allowSameDay: allowSameDay,
    calendarRef: calendarRef,
    didUseKeyNavigation: didUseKeyNavigation,
    endDate: endDate,
    hoveredDate: hoveredDate,
    isFlightCalendar: isFlightCalendar,
    maxDate: maxDate,
    maxDaysToSelect: maxDaysToSelect,
    maxSelectableDate: maxSelectableDate,
    minDate: minDate,
    scrollDate: scrollDate,
    selectTwoDates: selectTwoDates,
    startDate: startDate,
    tabClose: tabClose,
    handleKeyDismiss: handleKeyDismiss,
    handleKeyNav: handleKeyNav,
    onDayClicked: onDayClicked,
    onDayHovered: onDayHovered,
    setTabClose: setTabClose
  };
}
export default useCalendarForm;